'use client'

import React from 'react'

import { Flex } from '@chakra-ui/react'

import { AddButton } from '../Button/AddButton'
import { ColorScheme, Tag, TagProps } from '../Tag/Tag'
import { tagGroupStyles, tagGroupWrapStyles } from './TagGroup.styles'
import { GAP, SEARCH_INPUT_MIN_WIDTH } from './TagGroup.utils'

const ADD_BUTTON_SENTINEL = '__ADD__' as const
const ADD_INPUT_SENTINEL = '__ADD_INPUT__' as const
type RowItem =
  | React.ReactElement<TagProps>
  | typeof ADD_BUTTON_SENTINEL
  | typeof ADD_INPUT_SENTINEL

const WrapRow = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex alignItems='center' flexWrap='nowrap' gap={`${GAP}px`} width='100%'>
      {children}
    </Flex>
  )
}

export type TagGroupWrapProps = {
  refCallback: (node: HTMLDivElement | null) => void
  className?: string
  childArray: React.ReactElement<TagProps>[]
  rowBreaks: number[]
  effectiveMaxWidth: string | number
  editable: boolean
  onAdd?: () => void
  addInput?: React.ReactNode
  colorScheme?: ColorScheme
}

export const TagGroupWrap = ({
  refCallback,
  className,
  childArray,
  rowBreaks,
  effectiveMaxWidth,
  editable,
  onAdd,
  addInput,
  colorScheme,
}: TagGroupWrapProps) => {
  // Append a sentinel as the last virtual item so it lands on the correct row
  // as determined by computeRowBreaks.
  const allItems: RowItem[] = addInput
    ? [...childArray, ADD_INPUT_SENTINEL]
    : editable && onAdd
    ? [...childArray, ADD_BUTTON_SENTINEL]
    : [...childArray]

  return (
    <Flex
      className={className}
      ref={refCallback}
      {...tagGroupStyles}
      {...tagGroupWrapStyles}
    >
      {rowBreaks.map((startIdx, rowIdx) => {
        const rowItems = allItems.slice(
          startIdx,
          rowBreaks[rowIdx + 1] ?? allItems.length
        )
        return (
          <WrapRow key={rowIdx}>
            {rowItems.map((item, i) => {
              if (item === ADD_INPUT_SENTINEL) {
                return (
                  <span
                    key='add-input'
                    style={{
                      flex: 1,
                      minWidth: `min(${SEARCH_INPUT_MIN_WIDTH}px, 100%)`,
                      overflow: 'visible',
                    }}
                  >
                    {addInput}
                  </span>
                )
              }
              if (item === ADD_BUTTON_SENTINEL) {
                return (
                  <span key='add-button' style={{ flexShrink: 0 }}>
                    <AddButton colorScheme={colorScheme} onClick={onAdd} />
                  </span>
                )
              }
              return (
                <span
                  data-tag-item=''
                  key={item.key ?? startIdx + i}
                  style={{ maxWidth: '100%' }}
                >
                  <Tag
                    {...item.props}
                    colorScheme={colorScheme}
                    maxWidth={effectiveMaxWidth}
                  />
                </span>
              )
            })}
          </WrapRow>
        )
      })}
    </Flex>
  )
}
